/** Settings / Admin — v1's Settings view, ported around the org-settings data
 * that actually exists in the shared DB (mined live, not guessed):
 *   AUTOMATION {autoTag,priceDrop,stageTasks}  → Automation tab (editable)
 *   NR_MASTER  {ltv,rate,pts,…}                → ROI Defaults tab (editable)
 *   SCOPE_TPLS []                              → Templates tab (read)
 *   DIALPAD / QBO_SETTINGS / SHOWINGTIME_FEED / SCRAPER_JOBS → Integrations (read)
 *   profiles + role                            → Profile + Users tabs (read)
 *
 * Phase A: every tab READS and displays; the two editable tabs render real
 * controls but they're disabled with the "unlocks at go-live" note. Editing
 * writes the SHARED rec_org_settings (v1 reads it too), so it's gated behind
 * liveFire `settingsEdit` exactly like send/upload/create. Integration connects
 * and the destructive wipe tools stay in v1 (credentials / irreversible). */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Member } from "../team/api";
import { changeMyPassword, inviteMember, removeMember, setMemberRole, updateMyName } from "../team/api";
import { sowSeedMeta, type SowState } from "../sow/api";
import { SowSection } from "../sow/SowSection";
import { FarbarSettingsTab } from "../farbar/FarbarSettingsTab";
import type { CatalogMaterial } from "../materials/api";
import {
  cloneTplEntry, parseSharedTemplates, scopeTplsAppend, scopeTplsDelete, scopeTplsRename,
  scopeTplsUpdateEntry, sharedTplIndex, sowStateToTemplateEntry, templateEntryToSowState, type SowTemplate,
} from "../sow/templates";
import { fetchBackupStatus, fetchSnapshots, fmtSnapWhen, restoreSnapshot, runBackup, verifyBackup, type BackupStatus, type StateSnapshot } from "./backup";
import { BackendError } from "../../lib/backend";
import { ROLE_LABELS } from "../team/api";
import { liveOn } from "../../lib/liveFire";
import { NR_DEFAULTS } from "../roi/math";
import { NAV_PAGES, PAGE_LABELS, LEAD_FIELD_GROUPS, PERM_ACTIONS, LEAD_SCOPES, type Page } from "../../lib/access";
import {
  AUTOMATION_LABELS, ROI_DEFAULT_FIELDS, SETTINGS_MSG,
  narrowAutomation, narrowRoiMaster, type Automation, type Partner, type WritableSettingKey,
} from "./api";
import { PartnerAccessTab } from "./PartnerAccess";
import { QuoSms } from "./QuoSms";
import {
  createEsignTemplate, deleteEsignTemplate, fetchEsignConfig, fetchEsignTemplates,
  sendEsignTemplate, type EsignTemplate,
} from "../esign/api";
import { Icon, type IconName } from "../shell/icons";

type Tab = "profile" | "automation" | "roi" | "templates" | "contracts" | "users" | "partners" | "integrations" | "quo" | "data";

const TABS: { id: Tab; label: string; icon: IconName }[] = [
  { id: "profile", label: "Profile", icon: "user" },
  { id: "automation", label: "Automation", icon: "zap" },
  { id: "roi", label: "ROI Defaults", icon: "percent" },
  { id: "templates", label: "Templates", icon: "filetext" },
  { id: "contracts", label: "Contracts", icon: "clipboard" },
  { id: "users", label: "Users & Roles", icon: "shield" },
  { id: "partners", label: "Partner Access", icon: "usercheck" },
  { id: "integrations", label: "Integrations", icon: "plug" },
  { id: "quo", label: "Quo SMS", icon: "message" },
  { id: "data", label: "Data & Backups", icon: "database" },
];

/** v1 openInvite role menu (auth-users.js) — labels + descriptions verbatim. */
const INVITE_ROLES: { id: string; label: string; desc: string }[] = [
  { id: "admin", label: "Admin — full access", desc: "Full access to everything." },
  { id: "manager", label: "Manager — full access, no user/integration admin", desc: "Full access, minus user & integration administration." },
  { id: "acquisitions", label: "Acquisitions — assigned leads only", desc: "Sees only leads assigned to them; no financials." },
  { id: "tc", label: "Transaction Coordinator — assigned deals only", desc: "Assigned transactions + their contacts/emails." },
  { id: "dispo", label: "Dispo / Sales — wholesale & buyers", desc: "Wholesale pipeline and the cash-buyers list." },
  { id: "bookkeeper", label: "Bookkeeper — financials, read-only", desc: "Financials and expenses, read-only." },
  { id: "va", label: "Assistant / VA — assigned tasks only", desc: "Only tasks assigned to them." },
  { id: "member", label: "Member — legacy", desc: "Legacy role with frontend-only limits." },
];

export function SettingsPage({
  myProfile,
  members,
  orgSettings,
  onPutSetting,
  token,
  deals,
  onSavePartners,
  onSaveFeedUrl,
  onMembersChanged,
  onSaveScopeTpls,
  materials,
  onNeedMaterials,
}: {
  myProfile: Member | null;
  members: Member[];
  orgSettings: Record<string, unknown>;
  /** gated write (Phase B); undefined-safe caller updates orgSettings + outbox */
  onPutSetting: (key: WritableSettingKey, value: Record<string, unknown>) => void;
  token: string | null;
  /** Org deals for the Partner Access chips — {key: stable deal id, label:
   * address}. Wire from app.tsx: deals.map(d => ({ key: d.natKey, label: d.address })). */
  deals: { key: string; label: string }[];
  /** Gated whole-array persist of the PARTNERS blob (mirror of saveContractors
   * in app.tsx): setOrgSettings({...prev, PARTNERS: all}) + enqueue(opPutPartners(all)). */
  onSavePartners: (all: Partner[]) => void;
  /** Phase 27: save the ShowingTime feed url (settings.SHOWINGTIME_FEED). */
  onSaveFeedUrl?: (url: string) => void;
  /** Parity #3: refetch profiles after invite / role change / remove. */
  onMembersChanged?: () => Promise<void>;
  /** Scope Templates Library editing from Settings (v1 parity) — whole SCOPE_TPLS array. */
  onSaveScopeTpls?: (all: unknown[]) => void;
  /** Materials catalog + lazy-load trigger — so the Settings template editor has
   * the SAME material picker the deal SOW editor uses. */
  materials: CatalogMaterial[];
  onNeedMaterials: () => void;
}) {
  const [tab, setTab] = useState<Tab>("profile");
  const canEdit = liveOn("settingsEdit");

  return (
    <div className="settingspage">
      <div className="settingshd">
        <h1>Settings</h1>
        <p>
          Configure your account and organization
          {canEdit ? <em className="on"> · editing is live</em> : <em className="warn"> · {SETTINGS_MSG.editPhaseB}</em>}
        </p>
      </div>
      <div className="settingslayout">
        <nav className="settingsnav">
          {TABS.map((t) => (
            <button key={t.id} className={"settingsnavitem" + (tab === t.id ? " on" : "")} onClick={() => setTab(t.id)}>
              <Icon name={t.icon} size={17} className="setnavico" />
              <span>{t.label}</span>
            </button>
          ))}
        </nav>
        <div className="settingsbody">
          {tab === "profile" ? <ProfileTab me={myProfile} token={token} {...(onMembersChanged ? { onMembersChanged } : {})} /> : null}
          {tab === "automation" ? <AutomationTab raw={orgSettings["AUTOMATION"]} canEdit={canEdit} onPut={onPutSetting} /> : null}
          {tab === "roi" ? <RoiDefaultsTab raw={orgSettings["NR_MASTER"]} nrBy={orgSettings["NR_BY"]} canEdit={canEdit} onPut={onPutSetting} /> : null}
          {tab === "templates" ? <TemplatesTab orgSettings={orgSettings} token={token} canEdit={canEdit} materials={materials} onNeedMaterials={onNeedMaterials} {...(onSaveScopeTpls ? { onSaveScopeTpls } : {})} /> : null}
          {tab === "contracts" ? <FarbarSettingsTab token={token} isAdmin={(myProfile?.role ?? "").toLowerCase() === "admin"} /> : null}
          {tab === "users" ? <UsersTab members={members} me={myProfile} orgSettings={orgSettings} canEdit={canEdit} onPut={onPutSetting} token={token} {...(onMembersChanged ? { onMembersChanged } : {})} /> : null}
          {tab === "partners" ? <PartnerAccessTab raw={orgSettings["PARTNERS"]} deals={deals} canEdit={canEdit} onSave={onSavePartners} /> : null}
          {tab === "integrations" ? <IntegrationsTab orgSettings={orgSettings} canEdit={canEdit} {...(onSaveFeedUrl ? { onSaveFeedUrl } : {})} /> : null}
          {tab === "quo" ? <QuoSms token={token} dialpad={orgSettings["DIALPAD"]} canEdit={canEdit} onPutDialpad={(next) => onPutSetting("DIALPAD", next)} /> : null}
          {tab === "data" ? <DataTab token={token} isAdmin={(myProfile?.role ?? "").toLowerCase() === "admin"} /> : null}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Profile -- */
function ProfileTab({ me, token, onMembersChanged }: { me: Member | null; token: string | null; onMembersChanged?: () => Promise<void> }) {
  // hooks before the early return (rules of hooks — see App()'s sign-in gate)
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  if (!me) return <div className="empty gate"><strong>Not signed in.</strong><span>Your profile loads from the shared database.</span></div>;
  const role = ROLE_LABELS[me.role] ?? { label: me.role, desc: "" };

  const saveName = async () => {
    const nm = (nameDraft ?? "").trim();
    if (!nm || nm === (me.name ?? "") || busy) return;
    setBusy(true);
    setNote(null);
    try {
      await updateMyName(token, nm);
      setNote("Name updated — it shows on tasks, notes, and the team list.");
      setNameDraft(null);
      await onMembersChanged?.();
    } catch (e) {
      setNote(((e as { detail?: string; message?: string })?.detail ?? (e as Error)?.message ?? "Name change failed").slice(0, 120));
    } finally {
      setBusy(false);
    }
  };
  const savePw = async () => {
    if (busy) return;
    if (pw.a.length < 8) { setNote("Password needs at least 8 characters."); return; }
    if (pw.a !== pw.b) { setNote("The two passwords don't match."); return; }
    if (!token) { setNote("Sign in again first."); return; }
    setBusy(true);
    setNote(null);
    try {
      await changeMyPassword(token, pw.a);
      setNote("Password changed ✓ — use it on your next sign-in.");
      setPw({ a: "", b: "" });
    } catch (e) {
      setNote(((e as Error)?.message ?? "Password change failed").slice(0, 140));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="setsec">
      <h3>Your profile</h3>
      <div className="setgrid">
        <label className="setroifield"><span className="setroilabel">Name</span>
          <span className="setnamerow">
            <input className="setroiinput" value={nameDraft ?? me.name ?? ""} placeholder="Your display name" onChange={(e) => setNameDraft(e.target.value)} />
            <button className="btn slim primary" disabled={busy || nameDraft === null || !nameDraft.trim() || nameDraft.trim() === (me.name ?? "")} onClick={() => void saveName()}>{busy ? "…" : "Save"}</button>
          </span>
        </label>
        <Field label="Email" value={me.email} />
        <Field label="Role" value={`${role.label}${role.desc ? " — " + role.desc : ""}`} />
        <Field label="Status" value={me.status} />
      </div>

      <div className="setcard setpwcard">
        <div className="setcardhd"><strong>Change password</strong></div>
        <div className="setgrid">
          <label className="setroifield"><span className="setroilabel">New password</span>
            <input className="setroiinput" type="password" autoComplete="new-password" value={pw.a} onChange={(e) => setPw((p) => ({ ...p, a: e.target.value }))} />
          </label>
          <label className="setroifield"><span className="setroilabel">Repeat it</span>
            <input className="setroiinput" type="password" autoComplete="new-password" value={pw.b} onChange={(e) => setPw((p) => ({ ...p, b: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") void savePw(); }} />
          </label>
        </div>
        <div className="setactions">
          <button className="btn slim primary" disabled={busy || !pw.a || !pw.b} onClick={() => void savePw()}>{busy ? "Working…" : "Change password"}</button>
        </div>
        <p className="sub setnote">Changes your login password directly with the auth service — other signed-in devices stay signed in until their session expires. Email changes still go through v1 (they rewrite the login record).</p>
      </div>
      {note ? <p className="sub setusernote">{note}</p> : null}
    </div>
  );
}

/* ------------------------------------------------------------- Automation -- */
function AutomationTab({ raw, canEdit, onPut }: { raw: unknown; canEdit: boolean; onPut: (k: WritableSettingKey, v: Record<string, unknown>) => void }) {
  const auto = narrowAutomation(raw);
  const toggle = (key: keyof Automation) => {
    if (!canEdit) return;
    // Spread the RAW blob, not the narrowed one. Settings writes replace the
    // key whole (see opPut* / the door), and narrowAutomation returns only the
    // three toggles this tab renders — so spreading `auto` silently dropped
    // every other key AUTOMATION carries:
    //   emailScan → the Deal Inbox "Auto-scan every 5 min" toggle flips itself
    //               off and the Gmail scan loop stops
    //   fbRules   → the whole Facebook ruleset (looksLike, skip, keywords,
    //               blockers, areas, maxPrice, voice) reverts to DEFAULT_RULES
    // The other two writers to this key already merge; app.tsx and
    // facebook/rules.ts both carry the note "Settings writes replace the whole
    // key — so merge, never replace". This tab was the one that didn't.
    const base = raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};
    onPut("AUTOMATION", { ...base, [key]: !auto[key] });
  };
  return (
    <div className="setsec">
      <h3>Automation</h3>
      {!canEdit ? <div className="setgate">{SETTINGS_MSG.editPhaseB} — these show your current settings and will be editable at go-live.</div> : null}
      <div className="settoggles">
        {AUTOMATION_LABELS.map((a) => (
          <div key={a.key} className="settoggle">
            <div className="settogglelabel">
              <strong>{a.label}</strong>
              <span className="sub">{a.help}</span>
            </div>
            <button
              className={"switch" + (auto[a.key] ? " on" : "") + (canEdit ? "" : " locked")}
              disabled={!canEdit}
              onClick={() => toggle(a.key)}
              title={canEdit ? "" : SETTINGS_MSG.editPhaseB}
              aria-pressed={auto[a.key]}
            >
              <span className="knob" />
              <span className="switchtxt">{auto[a.key] ? "On" : "Off"}</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------- ROI Defaults -- */
function RoiDefaultsTab({ raw, nrBy, canEdit, onPut }: { raw: unknown; nrBy: unknown; canEdit: boolean; onPut: (k: WritableSettingKey, v: Record<string, unknown>) => void }) {
  const master = useMemo(() => narrowRoiMaster(raw), [raw]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  // v1 nrResetAll — wipe every per-property override so the org defaults above
  // apply everywhere again (Phase 29 door key NR_BY).
  const overrideCount = nrBy !== null && typeof nrBy === "object" && !Array.isArray(nrBy) ? Object.keys(nrBy as Record<string, unknown>).length : 0;
  const resetAll = () => {
    if (!canEdit) return;
    onPut("NR_BY", {});
    setConfirmReset(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const valueOf = (key: string) => (key in draft ? draft[key] ?? "" : master[key] !== undefined ? String(master[key]) : "");
  const dirty = Object.keys(draft).some((k) => Number(draft[k]) !== master[k]);

  const save = () => {
    if (!canEdit || !dirty) return;
    const next: Record<string, unknown> = { ...(raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {}) };
    for (const k of Object.keys(draft)) {
      const n = Number(draft[k]);
      if (Number.isFinite(n)) next[k] = n;
    }
    onPut("NR_MASTER", next);
    setDraft({});
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const groups = useMemo(() => {
    const m = new Map<string, typeof ROI_DEFAULT_FIELDS>();
    for (const f of ROI_DEFAULT_FIELDS) m.set(f.group, [...(m.get(f.group) ?? []), f]);
    return [...m.entries()];
  }, []);

  return (
    <div className="setsec">
      <h3>ROI defaults</h3>
      <p className="sub setnote">These seed the Net ROI calculator and the money-needed estimates on deal cards.</p>
      {!canEdit ? <div className="setgate">{SETTINGS_MSG.editPhaseB} — your current defaults are shown below.</div> : null}
      {groups.map(([group, fields]) => (
        <div key={group} className="setroigroup">
          <div className="setroigrouphd">{group}</div>
          <div className="setgrid">
            {fields.map((f) => (
              <label key={f.key} className="setroifield">
                <span className="setroilabel">{f.label} <em>{f.unit}</em></span>
                <input
                  className="setroiinput"
                  inputMode="decimal"
                  disabled={!canEdit}
                  value={valueOf(f.key)}
                  placeholder={master[f.key] !== undefined ? String(master[f.key]) : f.key in NR_DEFAULTS ? String(NR_DEFAULTS[f.key as keyof typeof NR_DEFAULTS]) : "—"}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                />
              </label>
            ))}
          </div>
        </div>
      ))}
      {canEdit ? (
        <div className="setactions">
          <button className="btn primary" disabled={!dirty} onClick={save}>Save ROI defaults</button>
          {overrideCount ? (
            confirmReset ? (
              <button className="btn slim danger" onClick={resetAll} title="Clears the per-property numbers on every deal — they fall back to the defaults above">Really reset {overrideCount} propert{overrideCount === 1 ? "y" : "ies"}?</button>
            ) : (
              <button className="btn slim" onClick={() => setConfirmReset(true)} title="v1 'Reset all overrides' — every deal falls back to these org defaults">Reset all property overrides ({overrideCount})</button>
            )
          ) : null}
          {saved ? <span className="sub on">{SETTINGS_MSG.savedLocal}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

/** Stable no-op door for the template editor: in template mode SowSection's ops
 * are discarded (edits live only in its in-memory state until "Save template"). */
const NO_OPS = (): void => {};

/** Scope Templates Library (v1 Settings → "Scope Templates Library"): the same
 * org-wide SOW templates you can save from a deal's Rehab → SOW tab. Editing one
 * opens the FULL Scope-of-Work editor (the exact SowSection a deal uses) in
 * template mode — material picker, copy items, rename, sections, markup, every
 * field — seeded from the template and saved back into the whole SCOPE_TPLS
 * array in v1's shape, so v1's modal keeps working. Needs onSaveScopeTpls +
 * the Phase-28 door + the materials catalog. */
function ScopeTemplatesLibrary({ orgSettings, canEdit, onSaveScopeTpls, materials, onNeedMaterials }: {
  orgSettings: Record<string, unknown>;
  canEdit: boolean;
  onSaveScopeTpls?: (all: unknown[]) => void;
  materials: CatalogMaterial[];
  onNeedMaterials: () => void;
}) {
  const raw = Array.isArray(orgSettings["SCOPE_TPLS"]) ? (orgSettings["SCOPE_TPLS"] as unknown[]) : [];
  const tpls = useMemo(() => parseSharedTemplates(raw), [raw]);
  const editable = canEdit && !!onSaveScopeTpls;
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameVal, setRenameVal] = useState("");
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  // The editor edits ONE template with the full SOW editor. edId is its shared
  // id ("v1tpl-N") or "__new__"; the seed SowState is stashed in a ref so the
  // editor loads it exactly once — a stable fetchSow keeps in-progress edits
  // from being wiped when the name field (parent state) re-renders.
  const [edId, setEdId] = useState<string | null>(null);
  const [edName, setEdName] = useState("");
  const seedRef = useRef<SowState | null>(null);
  const fetchSeed = useCallback(async () => seedRef.current, []);

  const tplItems = (t: SowTemplate) => t.cats.reduce((n, c) => n + c.items.length, 0);
  const uniqueName = (base: string): string => {
    const names = new Set(tpls.map((t) => t.name));
    if (!names.has(base)) return base;
    for (let i = 2; i < 999; i++) if (!names.has(`${base} ${i}`)) return `${base} ${i}`;
    return `${base} ${Date.now()}`;
  };

  const doRename = (id: string) => {
    const idx = sharedTplIndex(id);
    const nm = renameVal.trim();
    if (idx < 0 || !nm || !onSaveScopeTpls) { setRenaming(null); return; }
    onSaveScopeTpls(scopeTplsRename(raw, idx, nm));
    setRenaming(null);
  };
  const doDelete = (id: string) => {
    const idx = sharedTplIndex(id);
    if (idx < 0 || !onSaveScopeTpls) return;
    onSaveScopeTpls(scopeTplsDelete(raw, idx));
    setConfirmDel(null);
  };
  const doDuplicate = (t: SowTemplate) => {
    const copy = cloneTplEntry(raw, sharedTplIndex(t.id));
    if (!copy || !onSaveScopeTpls) return;
    copy["name"] = uniqueName(`${t.name} (copy)`);
    onSaveScopeTpls(scopeTplsAppend(raw, copy));
  };

  const openEd = (t: SowTemplate) => {
    const entry = raw[sharedTplIndex(t.id)];
    if (entry === undefined || entry === null) return;
    seedRef.current = templateEntryToSowState(entry);
    setEdName(t.name);
    setEdId(t.id);
  };
  const openNew = () => {
    if (!onSaveScopeTpls) return;
    seedRef.current = { meta: sowSeedMeta(), cats: [], items: [] };
    setEdName(uniqueName("New template"));
    setEdId("__new__");
  };
  const closeEd = () => { seedRef.current = null; setEdId(null); setEdName(""); };
  const saveEd = (sow: SowState) => {
    if (!onSaveScopeTpls) return;
    const entry = sowStateToTemplateEntry(edName.trim() || "Untitled template", sow, Date.now());
    if (edId && edId !== "__new__") onSaveScopeTpls(scopeTplsUpdateEntry(raw, sharedTplIndex(edId), entry));
    else onSaveScopeTpls(scopeTplsAppend(raw, entry));
    closeEd();
  };

  // While editing, the full SOW editor takes over the whole card.
  if (edId && seedRef.current) {
    return (
      <div className="setcard sowtpleditcard">
        <div className="sowtpledithd">
          <button className="btn slim" onClick={closeEd}>← Back to templates</button>
          <label className="sowtplnamewrap">Template name
            <input className="sowtplnameinput" value={edName} placeholder="e.g. Standard Full Rehab" onChange={(e) => setEdName(e.target.value)} />
          </label>
        </div>
        <p className="sub setnote">The same Scope-of-Work editor a deal uses — add categories &amp; rooms, pick materials from the catalog, copy items, set labor / material / markup, every field. “Save template” writes it for the whole team.</p>
        <SowSection
          dealKey={"__tpl__" + edId}
          fetchSow={fetchSeed}
          onOps={NO_OPS}
          materials={materials}
          onNeedMaterials={onNeedMaterials}
          templateMode
          onSaveTemplate={saveEd}
        />
      </div>
    );
  }

  return (
    <div className="setcard">
      <div className="setcardhd"><strong>🛠 Scope Templates Library</strong><span className="sub">{tpls.length} saved</span></div>
      <p className="sub setnote">Reusable Scope-of-Work templates — shared across all properties. Edit any one with the full SOW editor (materials, copy, rename, every field), or start a new one from scratch.</p>
      {editable ? <div className="sowtpllibacts"><button className="btn slim primary" onClick={openNew}>+ New template</button></div> : null}
      {tpls.length === 0 ? <p className="sub">No scope templates yet.</p> : (
        <ul className="setlist esigntpls">
          {tpls.map((t) => (
            <li key={t.id}>
              {renaming === t.id ? (
                <input className="sowtplrename" value={renameVal} autoFocus onChange={(e) => setRenameVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") doRename(t.id); if (e.key === "Escape") setRenaming(null); }} onBlur={() => doRename(t.id)} />
              ) : (
                <span className="esigntplname">{t.name}<em className="sub"> · {t.cats.length} categor{t.cats.length === 1 ? "y" : "ies"} · {tplItems(t)} items</em></span>
              )}
              {editable ? (
                <span className="esigntplacts">
                  <button className="btn xs" onClick={() => openEd(t)}>✎ Edit</button>
                  <button className="btn xs" title="Duplicate this template" onClick={() => doDuplicate(t)}>⧉ Copy</button>
                  <button className="btn xs" title="Rename for everyone" onClick={() => { setRenaming(t.id); setRenameVal(t.name); }}>Rename</button>
                  {confirmDel === t.id
                    ? <button className="sowdel confirm" onClick={() => doDelete(t.id)}>sure?</button>
                    : <button className="sowdel" title="Delete for the whole team" onClick={() => setConfirmDel(t.id)}>×</button>}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- Templates -- */
function TemplatesTab({ orgSettings, token, canEdit, onSaveScopeTpls, materials, onNeedMaterials }: { orgSettings: Record<string, unknown>; token: string | null; canEdit: boolean; onSaveScopeTpls?: (all: unknown[]) => void; materials: CatalogMaterial[]; onNeedMaterials: () => void }) {
  const docs = Array.isArray(orgSettings["DOC_TEMPLATES"]) ? (orgSettings["DOC_TEMPLATES"] as unknown[]) : [];
  const agent = orgSettings["AGENT"] as { camps?: unknown[] } | undefined;
  const camps = Array.isArray(agent?.camps) ? agent!.camps! : [];
  return (
    <div className="setsec">
      <h3>Templates</h3>
      <EsignTemplatesCard token={token} />
      <ScopeTemplatesLibrary orgSettings={orgSettings} canEdit={canEdit} materials={materials} onNeedMaterials={onNeedMaterials} {...(onSaveScopeTpls ? { onSaveScopeTpls } : {})} />
      <div className="setcard">
        <div className="setcardhd"><strong>Text opener scripts</strong><span className="sub">{camps.length} script{camps.length === 1 ? "" : "s"}</span></div>
        <p className="sub">Your Text Agent opener scripts (A/B variants) are managed on the Text Agent page.</p>
      </div>
      <div className="setcard">
        <div className="setcardhd"><strong>Document templates</strong><span className="sub">{docs.length} saved</span></div>
        {/* v1's DOC_TEMPLATES was a stub — store a {name, body} record; its
            docTemplates() had zero callers and nothing ever merged the
            {{address}} fields. The real document flow is BoldSign, above. */}
        <p className="sub">{docs.length ? `${docs.length} document template(s).` : "No document templates yet."} These are stored text only — send documents for signature with BoldSign above.</p>
      </div>
    </div>
  );
}

/** E-signature templates (BoldSign) — v1 parity item #1 (2026-07-23 audit).
 * List / create-from-PDF (opens BoldSign's embedded builder in a new tab) /
 * delete (two-click confirm) / send-from-template (inline signer form).
 * Everything runs through the same backend routes v1 uses. */
function EsignTemplatesCard({ token }: { token: string | null }) {
  const [tpls, setTpls] = useState<EsignTemplate[] | null>(null);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [sendFor, setSendFor] = useState<EsignTemplate | null>(null);
  const [sName, setSName] = useState("");
  const [sEmail, setSEmail] = useState("");
  const [sTitle, setSTitle] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    setNote(null);
    void fetchEsignConfig(token).then((ok) => {
      setConfigured(ok);
      if (!ok) { setTpls([]); return; }
      void fetchEsignTemplates(token)
        .then(setTpls)
        .catch((e) => { setTpls([]); setNote((e as { detail?: string })?.detail ?? "Couldn't load templates"); });
    });
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const createTpl = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f || busy) return;
    setBusy(true);
    setNote(null);
    try {
      const url = await createEsignTemplate(token, f, newTitle.trim() || f.name.replace(/\.pdf$/i, ""));
      window.open(url, "_blank", "noopener");
      setNote("Builder opened in a new tab — place the fields, hit Save there, then ⟳ here.");
      setNewTitle("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      setNote((e as { detail?: string })?.detail ?? "Couldn't start the template builder");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (id: string) => {
    if (busy) return;
    setBusy(true);
    setNote(null);
    try {
      await deleteEsignTemplate(token, id);
      setTpls((prev) => (prev ?? []).filter((t) => t.templateId !== id));
    } catch (e) {
      setNote((e as { detail?: string })?.detail ?? "Delete failed");
    } finally {
      setConfirmDel(null);
      setBusy(false);
    }
  };

  const doSend = async () => {
    if (!sendFor || busy) return;
    const name = sName.trim(), email = sEmail.trim();
    if (!name || !email.includes("@")) { setNote("Signer name + a valid email first"); return; }
    setBusy(true);
    setNote(null);
    try {
      const docId = await sendEsignTemplate(token, {
        templateId: sendFor.templateId,
        title: sTitle.trim() || sendFor.title,
        signers: [{ name, email }],
      });
      setNote(`Sent to ${email}${docId ? ` — it's in the e-sign log (${docId.slice(0, 8)}…)` : ""}. Signed PDFs auto-file to Drive.`);
      setSendFor(null);
      setSName(""); setSEmail(""); setSTitle("");
    } catch (e) {
      setNote((e as { detail?: string })?.detail ?? "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="setcard">
      <div className="setcardhd">
        <strong>E-signature templates (BoldSign)</strong>
        <span className="sub">{tpls === null ? "loading…" : `${tpls.length} saved`}</span>
        <button className="btn slim" onClick={load} disabled={busy} title="Refresh the template list">⟳</button>
      </div>
      {configured === false ? (
        <p className="sub">BoldSign isn't configured on the backend (BOLDSIGN_API_KEY). One-off sends and templates both need it.</p>
      ) : (
        <>
          {tpls !== null && tpls.length === 0 && configured ? <p className="sub">No templates yet — create one from a PDF below.</p> : null}
          {tpls?.length ? (
            <ul className="setlist esigntpls">
              {tpls.map((t) => (
                <li key={t.templateId}>
                  <span className="esigntplname">{t.title}</span>
                  <span className="esigntplacts">
                    <button className="btn slim" disabled={busy} onClick={() => { setSendFor(sendFor?.templateId === t.templateId ? null : t); setSTitle(t.title); setNote(null); }}>Send</button>
                    {confirmDel === t.templateId ? (
                      <button className="btn slim danger" disabled={busy} onClick={() => void doDelete(t.templateId)}>Really delete?</button>
                    ) : (
                      <button className="btn slim" disabled={busy} title="Delete this template" onClick={() => setConfirmDel(t.templateId)}>✕</button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          {sendFor ? (
            <div className="esignsendform">
              <strong className="sub">Send “{sendFor.title}”</strong>
              <input placeholder="Signer name" value={sName} onChange={(e) => setSName(e.target.value)} />
              <input placeholder="signer@email.com" value={sEmail} onChange={(e) => setSEmail(e.target.value)} />
              <input placeholder="Document title" value={sTitle} onChange={(e) => setSTitle(e.target.value)} />
              <div className="esignsendacts">
                <button className="btn slim primary" disabled={busy} onClick={() => void doSend()}>{busy ? "Sending…" : "Send for signature"}</button>
                <button className="btn slim" disabled={busy} onClick={() => setSendFor(null)}>Cancel</button>
              </div>
            </div>
          ) : null}
          <div className="esignnewtpl">
            <input ref={fileRef} type="file" accept="application/pdf" />
            <input placeholder="Template name (e.g. Purchase Agreement)" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
            <button className="btn slim primary" disabled={busy} onClick={() => void createTpl()}>{busy ? "Working…" : "+ New template from PDF"}</button>
          </div>
          <p className="sub setnote">The builder opens in a BoldSign tab — drop the signature fields, Save, then refresh here. Sending uses the saved fields; the signed PDF files itself to the property's Drive folder.</p>
        </>
      )}
      {note ? <p className="sub esignnote">{note}</p> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ Users -- */
function UsersTab({ members, me, orgSettings, canEdit, onPut, token, onMembersChanged }: {
  members: Member[];
  me: Member | null;
  orgSettings: Record<string, unknown>;
  canEdit: boolean;
  onPut: (key: WritableSettingKey, value: Record<string, unknown>) => void;
  token: string | null;
  /** refetch profiles after an admin write (invite/role/remove) */
  onMembersChanged?: () => Promise<void>;
}) {
  const isAdmin = (me?.role ?? "").toLowerCase() === "admin";
  const permsRaw = orgSettings["PERMS"];
  const perms: Record<string, unknown> = permsRaw !== null && typeof permsRaw === "object" && !Array.isArray(permsRaw) ? (permsRaw as Record<string, unknown>) : {};
  const [expanded, setExpanded] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inv, setInv] = useState({ name: "", email: "", role: "member", access: "view" });
  const [uBusy, setUBusy] = useState(false);
  const [uNote, setUNote] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const err = (e: unknown, fallback: string) =>
    setUNote(((e as { detail?: string; message?: string })?.detail ?? (e as Error)?.message ?? fallback).slice(0, 140));

  const entryOf = (userId: string | null): Record<string, unknown> => {
    if (!userId) return {};
    const e = perms[userId];
    return e && typeof e === "object" && !Array.isArray(e) ? (e as Record<string, unknown>) : {};
  };
  const arrOf = (userId: string | null, key: string): string[] => {
    const v = entryOf(userId)[key];
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  };
  const hiddenOf = (userId: string | null): string[] => arrOf(userId, "hidden");
  const leadScopeOf = (userId: string | null): string => {
    const v = entryOf(userId)["leadScope"];
    return v === "assigned" ? "assigned" : "all";
  };
  const hiddenFieldsOf = (userId: string | null): string[] => arrOf(userId, "hiddenFields");

  // Merge one key into a user's PERMS entry, preserving the others (page-hiding
  // and field-hiding share the same entry).
  const writePerm = (userId: string, key: string, next: string[]) => {
    if (!canEdit) return;
    onPut("PERMS", { ...perms, [userId]: { ...entryOf(userId), [key]: next } });
  };
  // Per-section access LEVEL (v1 None/View/Edit). Stored at entry.access[page];
  // "edit" is the default so it's omitted (keeps the blob small). Writing also
  // clears any stale legacy `hidden[]` entry for that page so the two models
  // never disagree.
  const accessOf = (userId: string | null, page: string): "none" | "view" | "edit" => {
    const e = entryOf(userId);
    if (Array.isArray(e["hidden"]) && (e["hidden"] as unknown[]).includes(page)) return "none";
    const acc = e["access"];
    const v = acc !== null && typeof acc === "object" && !Array.isArray(acc) ? (acc as Record<string, unknown>)[page] : undefined;
    return v === "none" || v === "view" ? v : "edit";
  };
  const setPageAccess = (userId: string, page: string, level: "none" | "view" | "edit") => {
    if (!canEdit) return;
    const e = entryOf(userId);
    const accRaw = e["access"];
    const acc: Record<string, unknown> = accRaw !== null && typeof accRaw === "object" && !Array.isArray(accRaw) ? { ...(accRaw as Record<string, unknown>) } : {};
    if (level === "edit") delete acc[page]; else acc[page] = level;
    const hidden = (Array.isArray(e["hidden"]) ? (e["hidden"] as unknown[]).filter((x) => x !== page) : []);
    onPut("PERMS", { ...perms, [userId]: { ...e, access: acc, hidden } });
  };
  const toggleHideField = (userId: string, group: string) => {
    const cur = hiddenFieldsOf(userId);
    writePerm(userId, "hiddenFields", cur.includes(group) ? cur.filter((g) => g !== group) : [...cur, group]);
  };
  // action capabilities (PERMS[id].actions{}) + lead scope (PERMS[id].leadScope)
  const actionsOf = (userId: string | null): Record<string, unknown> => {
    const a = entryOf(userId)["actions"];
    return a !== null && typeof a === "object" && !Array.isArray(a) ? (a as Record<string, unknown>) : {};
  };
  const toggleAction = (userId: string, key: string) => {
    if (!canEdit) return;
    const cur = actionsOf(userId);
    onPut("PERMS", { ...perms, [userId]: { ...entryOf(userId), actions: { ...cur, [key]: !cur[key] } } });
  };
  const setLeadScope = (userId: string, scope: string) => {
    if (!canEdit) return;
    onPut("PERMS", { ...perms, [userId]: { ...entryOf(userId), leadScope: scope } });
  };

  // Every page except dashboard (never hideable) can be restricted.
  const HIDEABLE = (NAV_PAGES as readonly Page[]).filter((p) => p !== "dashboard");

  const doInvite = async () => {
    const email = inv.email.trim().toLowerCase();
    if (!email.includes("@") || uBusy) { setUNote("A valid email first"); return; }
    setUBusy(true);
    setUNote(null);
    try {
      const newId = await inviteMember(token, { email, name: inv.name.trim(), role: inv.role });
      // "No sections" default: pre-hide every page for the new user id, so the
      // account starts locked down and gets opened up deliberately.
      if (inv.access === "none" && newId && canEdit) {
        onPut("PERMS", { ...perms, [newId]: { hidden: [...HIDEABLE] } });
      }
      setUNote(`Invite email sent to ${email} — they set a password from that link and land in this org as ${ROLE_LABELS[inv.role]?.label ?? inv.role}.`);
      setInv({ name: "", email: "", role: "member", access: "view" });
      await onMembersChanged?.();
    } catch (e) {
      err(e, "Invite failed");
    } finally {
      setUBusy(false);
    }
  };
  const doRole = async (userId: string, role: string) => {
    if (uBusy) return;
    setUBusy(true);
    setUNote(null);
    try {
      await setMemberRole(token, userId, role);
      await onMembersChanged?.();
    } catch (e) {
      err(e, "Role change failed");
    } finally {
      setUBusy(false);
    }
  };
  const doRemove = async (userId: string) => {
    if (uBusy) return;
    setUBusy(true);
    setUNote(null);
    try {
      await removeMember(token, userId);
      setUNote("Removed — their login stops working everywhere immediately.");
      await onMembersChanged?.();
    } catch (e) {
      err(e, "Remove failed");
    } finally {
      setConfirmRemove(null);
      setUBusy(false);
    }
  };

  const visibleMembers = members.filter((m) => m.status !== "removed");
  const removedCount = members.length - visibleMembers.length;

  return (
    <div className="setsec">
      <div className="setuserhd">
        <h3>Users &amp; roles</h3>
        {isAdmin ? <button className="btn slim" onClick={() => setInviteOpen((v) => !v)}>{inviteOpen ? "Close" : "+ Invite member"}</button> : null}
      </div>

      {isAdmin && inviteOpen ? (
        <div className="setinvite">
          <div className="setinvitegrid">
            <label className="wide"><span>Full name</span><input value={inv.name} placeholder="e.g. Dana Ramirez" onChange={(e) => setInv((p) => ({ ...p, name: e.target.value }))} /></label>
            <label className="wide"><span>Email</span><input value={inv.email} type="email" placeholder="name@email.com" onChange={(e) => setInv((p) => ({ ...p, email: e.target.value }))} /></label>
            <label><span>Role</span>
              <select value={inv.role} onChange={(e) => setInv((p) => ({ ...p, role: e.target.value }))}>
                {INVITE_ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </label>
            <label><span>Default access</span>
              <select value={inv.access} onChange={(e) => setInv((p) => ({ ...p, access: e.target.value }))}>
                <option value="view">View all sections</option>
                <option value="edit">Edit all sections</option>
                <option value="none">No sections (set later)</option>
              </select>
            </label>
          </div>
          <div className="setinvitefoot">
            <button className="btn slim primary" disabled={uBusy || !inv.email.trim()} onClick={() => void doInvite()}>{uBusy ? "Sending…" : "Send invite email"}</button>
            <span className="sub">{INVITE_ROLES.find((r) => r.id === inv.role)?.desc}</span>
          </div>
          <p className="sub setnote">They get a login-link email from the CRM's auth service, set their own password, and land in this org with the role above — no invite codes. Roles other than Admin/Manager are locked at the database.</p>
        </div>
      ) : null}
      {uNote ? <p className="sub setusernote">{uNote}</p> : null}

      <div className="setuserlist">
        {removedCount ? <p className="sub">{removedCount} removed member{removedCount === 1 ? "" : "s"} hidden.</p> : null}
        {visibleMembers.length === 0 ? <p className="sub">No team members loaded.</p> : visibleMembers.map((m) => {
          const role = ROLE_LABELS[m.role] ?? { label: m.role, desc: "" };
          const mine = me && m.email === me.email;
          const mAdmin = (m.role ?? "").toLowerCase() === "admin";
          // Admins can't be restricted; only an admin can edit; need a user id to key PERMS.
          const adminActs = isAdmin && !mine && !!m.id; // role change + remove (server guards the last admin)
          // The per-section access panel is offered for EVERY manageable teammate,
          // admins included (v1 parity — the None/View/Edit option was always
          // visible). For an admin it renders read-only under a note, because an
          // admin is never restricted until you switch them to a non-admin role.
          const canOpenAccess = isAdmin && canEdit && !mine && !!m.id;
          const restricted = HIDEABLE.filter((p) => accessOf(m.id, p) !== "edit").length;
          const open = expanded === m.id;
          return (
            <div key={m.email} className="setuserrow2">
              <div className="setuserhead">
                <div className="setuserwho">
                  <strong>{m.name ?? (m.email.split("@")[0] ?? m.email)}{mine ? <em className="sub"> · you</em> : null}</strong>
                  <span className="sub">{m.email}</span>
                </div>
                {adminActs ? (
                  <select className="setroleselect" value={m.role} disabled={uBusy} title="Change role (database-enforced for restricted roles)" onChange={(e) => void doRole(m.id!, e.target.value)}>
                    {INVITE_ROLES.map((r) => <option key={r.id} value={r.id}>{ROLE_LABELS[r.id]?.label ?? r.id}</option>)}
                  </select>
                ) : (
                  <span className="rolepill">{role.label}</span>
                )}
                <span className={"teamstatus" + (m.status === "active" ? " on" : "")}>{m.status === "active" ? "Active" : m.status}</span>
                {canOpenAccess ? (
                  <button className="btn slim" onClick={() => setExpanded(open ? null : (m.id ?? null))}>
                    {mAdmin ? "Section access" : restricted ? `${restricted} section${restricted === 1 ? "" : "s"} limited` : "Set access"} {open ? "▾" : "▸"}
                  </button>
                ) : mAdmin ? <span className="sub">full access</span> : null}
                {adminActs ? (
                  confirmRemove === m.id ? (
                    <button className="btn slim danger" disabled={uBusy} title="Their login stops working everywhere, immediately" onClick={() => void doRemove(m.id!)}>Really remove?</button>
                  ) : (
                    <button className="btn slim" disabled={uBusy} title="Remove from the org (soft — history stays)" onClick={() => setConfirmRemove(m.id!)}>Remove</button>
                  )
                ) : null}
              </div>
              {canOpenAccess && open ? (
                <div className={"setperms" + (mAdmin ? " setpermsro" : "")}>
                  {mAdmin ? (
                    <p className="setadminnote"><strong>{m.name ?? "This person"} is an Admin</strong> — admins always have full access to every section, so the limits below don't apply to them yet. Change their role above to a non-admin role and these None / View / Edit choices take effect.</p>
                  ) : null}
                  <p className="sub setpermshd">Section access — <strong>None</strong> hides it, <strong>View</strong> is read-only, <strong>Edit</strong> is full. Dashboard always stays visible. Changes take effect on their next load; test with a spare login before relying on it.</p>
                  <div className="setaccessrows">
                    {HIDEABLE.map((p) => {
                      const lvl = accessOf(m.id, p);
                      return (
                        <div key={p} className="setaccessrow">
                          <span className="setaccessname">{PAGE_LABELS[p]}</span>
                          <div className="setaccessseg">
                            {(["none", "view", "edit"] as const).map((v) => (
                              <button key={v} disabled={mAdmin} className={"setaccessbtn " + v + (lvl === v ? " on" : "")} onClick={() => setPageAccess(m.id!, p, v)}>
                                {v === "none" ? "None" : v === "view" ? "View" : "Edit"}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="sub setpermshd">Hide sensitive fields — hidden from {m.name ?? "this user"} on every property.</p>
                  <div className="setpermsgrid">
                    {LEAD_FIELD_GROUPS.map((g) => {
                      const isHidden = hiddenFieldsOf(m.id).includes(g.key);
                      return (
                        <label key={g.key} className="setpermitem" title={g.desc}>
                          <input type="checkbox" disabled={mAdmin} checked={isHidden} onChange={() => toggleHideField(m.id!, g.key)} />
                          <span className={isHidden ? "" : "dim"}>Hide {g.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="sub setpermshd">What {m.name ?? "this user"} can do — untick to take a capability away.</p>
                  <div className="setpermsgrid">
                    {PERM_ACTIONS.map((a) => {
                      const on = actionsOf(m.id)[a.key] === true;
                      return (
                        <label key={a.key} className="setpermitem" title={a.desc}>
                          <input type="checkbox" disabled={mAdmin} checked={on} onChange={() => toggleAction(m.id!, a.key)} />
                          <span className={on ? "" : "dim"}>{a.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="setpermscope">
                    <span className="sub setpermshd">Lead visibility</span>
                    <select className="setroleselect" disabled={mAdmin} value={leadScopeOf(m.id)} onChange={(e) => setLeadScope(m.id!, e.target.value)}>
                      {LEAD_SCOPES.map((sc) => <option key={sc.key} value={sc.key}>{sc.label}</option>)}
                    </select>
                    <span className="sub">“Only assigned” limits them to leads/deals assigned to them — enforced by the database roles for restricted users; this UI flag mirrors that.</span>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <p className="sub setnote">Invites, role changes, and removals are live here (admin-only; the last admin can never be demoted or removed). Per-section None / View / Edit, field hiding, capabilities, and lead scope apply in the app; for the 5 restricted roles the database is the ultimate boundary (they can only load what their role allows, even via dev tools). Admins are never restricted.</p>
    </div>
  );
}

/* ----------------------------------------------------------- Integrations -- */
function IntegrationsTab({ orgSettings, canEdit, onSaveFeedUrl }: {
  orgSettings: Record<string, unknown>;
  canEdit: boolean;
  onSaveFeedUrl?: (url: string) => void;
}) {
  const dial = orgSettings["DIALPAD"] as Record<string, unknown> | undefined;
  const qbo = orgSettings["QBO_SETTINGS"] as Record<string, unknown> | undefined;
  const feed = typeof orgSettings["SHOWINGTIME_FEED"] === "string" ? (orgSettings["SHOWINGTIME_FEED"] as string) : "";
  const scrapers = orgSettings["SCRAPER_JOBS"] as Record<string, unknown> | undefined;
  const scraperCount = scrapers ? Object.values(scrapers).reduce<number>((n, v) => n + (Array.isArray(v) ? v.length : 0), 0) : 0;

  const quoConnected = !!(dial && (dial["apiKeySet"] === true || dial["secretSet"] === true));
  const qboConnected = !!(qbo && Object.keys(qbo).length > 0);

  // ShowingTime feed url — the one editable integration (Phase 27 door key).
  const [feedDraft, setFeedDraft] = useState(feed);
  useEffect(() => { setFeedDraft(feed); }, [feed]);
  const feedDirty = feedDraft.trim() !== feed;
  const feedSave = () => {
    if (!onSaveFeedUrl || !feedDirty) return;
    onSaveFeedUrl(feedDraft.trim());
  };

  return (
    <div className="setsec">
      <h3>Integrations</h3>
      <div className="setgate">{SETTINGS_MSG.integrationV1}</div>
      <Integration name="Text / SMS (Quo)" connected={quoConnected} detail={quoConnected ? `Signature: ${typeof dial?.["signature"] === "string" ? dial["signature"] : "set"}` : "Not connected"} />
      <Integration name="Google Drive & Gmail" connected={null} detail="Connected per-account on the backend. Reconnect on go-live day." />
      <Integration name="QuickBooks Online" connected={qboConnected} detail={qboConnected ? "Connected" : "Never connected — expenses are empty."} />
      <div className="setintegration">
        <div className="setintwho">
          <strong>ShowingTime feed</strong>
          <span className="sub">{feed ? "Calendar feed configured — showings render on each deal's Showings tab" : "Paste your ShowingTime calendar-feed url to see showings on deals"}</span>
          <input
            className="setfeedinput"
            placeholder="https://scheduling.showingtime.com/…/feed.ics"
            value={feedDraft}
            disabled={!canEdit || !onSaveFeedUrl}
            onChange={(e) => setFeedDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") feedSave(); }}
          />
        </div>
        <span className={"setintstatus " + (feed ? "on" : "off")}>{feed ? "Connected" : "Not connected"}</span>
        <button className="btn slim" disabled={!canEdit || !onSaveFeedUrl || !feedDirty} title={onSaveFeedUrl ? "Save the feed url (needs the Phase-27 door migration)" : "Wire onSaveFeedUrl"} onClick={feedSave}>Save</button>
      </div>
      <Integration name="Scrapers" connected={scraperCount > 0} detail={`${scraperCount} scraper job${scraperCount === 1 ? "" : "s"} configured — runs on the backend`} />
    </div>
  );
}

function Integration({ name, connected, detail }: { name: string; connected: boolean | null; detail: string }) {
  return (
    <div className="setintegration">
      <div className="setintwho">
        <strong>{name}</strong>
        <span className="sub">{detail}</span>
      </div>
      <span className={"setintstatus " + (connected === null ? "muted" : connected ? "on" : "off")}>
        {connected === null ? "Backend" : connected ? "Connected" : "Not connected"}
      </span>
      <button className="btn slim" disabled title={SETTINGS_MSG.integrationV1}>Connect — Phase B</button>
    </div>
  );
}

/* --------------------------------------------------------- Data & Backups -- */
function DataTab({ token, isAdmin }: { token: string | null; isAdmin: boolean }) {
  const [st, setSt] = useState<BackupStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<"run" | "verify" | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = () => {
    setLoading(true); setErr(null);
    void fetchBackupStatus(token)
      .then(setSt)
      .catch((e) => setErr(e instanceof BackendError ? e.detail : "Could not reach the backend"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const run = async () => {
    setBusy("run"); setNote(null);
    try {
      const r = await runBackup(token);
      setNote(r.ok ? "Backup completed and filed to Drive ✓" : "Backup ran but reported: " + JSON.stringify(r.raw).slice(0, 140));
      load();
    } catch (e) { setNote(e instanceof BackendError ? "Backup failed: " + e.detail : "Backup failed"); }
    finally { setBusy(null); }
  };
  const verify = async () => {
    setBusy("verify"); setNote(null);
    try {
      const r = await verifyBackup(token);
      setNote(r.ok ? "Latest backup verified — all tables present ✓" : "Verify found an issue: " + JSON.stringify(r.raw).slice(0, 160));
    } catch (e) { setNote(e instanceof BackendError ? "Verify failed: " + e.detail : "Verify failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="setsec">
      <h3>Data &amp; backups</h3>
      <div className="setcard">
        <div className="setcardhd"><strong>Backups &amp; restore</strong></div>
        <p className="sub">
          {loading ? "Checking backup status…"
            : err ? err
            : !st?.driveConnected ? "Google Drive isn't connected — connect it (Business Files / Drive) before running a backup."
            : st.last ? `Last backup: ${st.last.name || st.last.when || "—"}${st.last.tables ? ` · ${st.last.tables} tables` : ""}.`
            : "Drive is connected. No backup has run yet."}
        </p>
        <div className="setrow">
          <button className="btn slim primary" onClick={run} disabled={busy !== null || !st?.driveConnected}>{busy === "run" ? "Backing up…" : "Run backup now"}</button>
          <button className="btn slim" onClick={verify} disabled={busy !== null || !st?.last}>{busy === "verify" ? "Verifying…" : "Verify latest"}</button>
          <button className="btn slim" onClick={load} disabled={loading}>Refresh</button>
        </div>
        {note ? <div className="inbxnote">{note}</div> : null}
        <p className="sub setnote">A backup writes a dated copy of the database to your connected Drive. It's non-destructive — it never changes live data{isAdmin ? "" : " (restoring a snapshot is admin-only)"}.</p>
      </div>
      {isAdmin ? <SnapshotsCard token={token} /> : null}
      <div className="setcard danger">
        <div className="setcardhd"><strong>Start Fresh · Wipe Everything · Reset Local Data</strong></div>
        <p className="sub">These permanently erase data and are intentionally not built into v2 — they'd affect v1 too, since both share one database. Use v1 if you ever need them.</p>
      </div>
    </div>
  );
}

/** State snapshots + GUARDED restore (parity #5, 2026-07-23). The 2026-07-23
 * incident proved raw blob-restore is a footgun: restoring re-applies the
 * whole org state, so anything created since gets swept. This UI makes the
 * blast radius explicit (counts + age), requires typing RESTORE, and leans on
 * the database mass-tombstone guard: a restore that would tombstone >20% of a
 * record table is BLOCKED server-side — that block is a feature, not a bug. */
function SnapshotsCard({ token }: { token: string | null }) {
  const [snaps, setSnaps] = useState<StateSnapshot[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pick, setPick] = useState<StateSnapshot | null>(null);
  const [arm, setArm] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const load = () => {
    setErr(null);
    void fetchSnapshots(token)
      .then(setSnaps)
      .catch((e) => { setSnaps([]); setErr(e instanceof BackendError ? e.detail : "Couldn't load snapshots"); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const doRestore = async () => {
    if (!pick || arm !== "RESTORE" || busy) return;
    setBusy(true);
    setNote(null);
    try {
      const r = await restoreSnapshot(token, pick.id);
      setNote(`Restored from ${fmtSnapWhen(pick.savedAt)} ✓ — reloading so every list rereads…`);
      void r;
      setTimeout(() => location.reload(), 2500);
    } catch (e) {
      const d = e instanceof BackendError ? e.detail : (e as Error)?.message ?? "Restore failed";
      setNote(d.includes("mass_tombstone_blocked")
        ? "The database guard BLOCKED this restore — it would have swept a large slice of a record table (that guard exists because of the July 23 incident). If this restore is truly deliberate, run it from the SQL editor with the guard cap raised, or ask Claude to walk through it."
        : "Restore failed: " + d.slice(0, 160));
    } finally {
      setBusy(false);
      setArm("");
      setPick(null);
    }
  };

  return (
    <div className="setcard">
      <div className="setcardhd"><strong>State snapshots (restore)</strong><button className="btn slim" onClick={load}>⟳</button></div>
      <p className="sub">The server snapshots the whole org state every 15 minutes (kept ~14 days). Restoring rewinds <em>everything</em> to that moment — records created after it are swept, which is why restore is guarded twice: you type RESTORE here, and the database refuses reverts that would mass-delete a table.</p>
      {err ? <p className="sub">{err}</p> : null}
      {snaps === null ? <p className="sub">Loading snapshots…</p> : snaps.length === 0 ? <p className="sub">No snapshots yet.</p> : (
        <ul className="setlist snaplist">
          {snaps.slice(0, 12).map((s) => (
            <li key={s.id}>
              <span className="esigntplname">{fmtSnapWhen(s.savedAt)}<em className="sub"> · {s.total !== null ? `${s.total} records` : "—"}{s.tablesTotal !== null ? ` · tables ${s.tablesTotal}` : ""}</em></span>
              <button className="btn xs" onClick={() => { setPick(pick?.id === s.id ? null : s); setArm(""); setNote(null); }}>{pick?.id === s.id ? "Cancel" : "Restore…"}</button>
            </li>
          ))}
        </ul>
      )}
      {pick ? (
        <div className="snaprestore">
          <p className="sub"><strong>Restoring {fmtSnapWhen(pick.savedAt)}.</strong> Everything the whole team changed since then — new leads, edits, payments, texts logged — reverts. A pre-restore snapshot is taken automatically, so this action is itself undoable. Type <code>RESTORE</code> to arm the button.</p>
          <div className="setrow">
            <input className="setroiinput snaparm" value={arm} placeholder="RESTORE" onChange={(e) => setArm(e.target.value.toUpperCase())} />
            <button className="btn slim danger" disabled={arm !== "RESTORE" || busy} onClick={() => void doRestore()}>{busy ? "Restoring…" : "Restore this snapshot"}</button>
          </div>
        </div>
      ) : null}
      {note ? <p className="sub setusernote">{note}</p> : null}
    </div>
  );
}

/* ----------------------------------------------------------------- shared -- */
function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="setfield">
      <span className="setfieldlabel">{label}</span>
      <span className="setfieldval">{value}</span>
    </div>
  );
}
